import React, { useRef, useState, useEffect } from 'react'

import Webcam from 'react-webcam'

import {
  CloseButton,
  CameraContainer,
  CameraOverlay,
  FlashEffect,
  RectangleOverlay,
  Controls,
  CaptureButton,
  Logo,
  SwitchCameraButton,
  ControlWrapDiv,
  Tooltip,
  TooltipText,
  CameraWrap,
} from './CameraScan.style'
import Icon from 'components/Icon'
import PreviewModal from '../PreviewModal'
import ResultsPreviewModal from '../ResultsPreviewModal/ResultsPreviewModal'
import { PATRON_SCAN } from 'utils/constants'
import useOnlineStatus from 'hooks/useOnline'
import { scanImage } from 'api/patronscan'

const CameraScan = () => {
  const isOnline = useOnlineStatus()

  const webcamRef = useRef(null)
  const [capturedImage, setCapturedImage] = useState(null)

  const [flashEffect, setFlashEffect] = useState(false)

  const [isCameraOpen, setIsCameraOpen] = useState(false) // Toggle camera UI

  const [isResultPreviewOpen, setIsResultPreviewClose] = useState(false)

  const [patronScanResponse, setPatronScanResponse] = useState({})
  const [patronScanError, setPatronScanError] = useState({})
  const [isLoading, setIsLoading] = useState(false)

  const MAX_WIDTH = 3000
  const MAX_HEIGHT = 4000
  const capturePhoto = () => {
    setFlashEffect(true)

    setTimeout(() => setFlashEffect(false), 300) // Flash animation

    const video = webcamRef.current.video
    if (!video) {
      console.log('Video not ready')
      return
    }

    const originalWidth = video.videoWidth
    const originalHeight = video.videoHeight

    // Check if we need to scale
    const needsScaling = originalWidth > MAX_WIDTH || originalHeight > MAX_HEIGHT

    const scale = needsScaling ? Math.min(MAX_WIDTH / originalWidth, MAX_HEIGHT / originalHeight) : 1

    const finalWidth = Math.floor(originalWidth * scale)
    const finalHeight = Math.floor(originalHeight * scale)

    const canvas = document.createElement('canvas')
    canvas.width = finalWidth
    canvas.height = finalHeight

    const ctx = canvas.getContext('2d')
    ctx.drawImage(video, 0, 0, finalWidth, finalHeight)

    const imageSrc = canvas.toDataURL('image/png', 1)
    const base64Data = imageSrc.split(',')[1]
    const byteSize = (base64Data.length * 3) / 4
    const mbSize = (byteSize / (1024 * 1024)).toFixed(3)

    console.log(`Captured image resolution: ${finalWidth}x${finalHeight}`)
    console.log(`Captured image size: ${mbSize} MB`)

    // Clean up
    ctx.clearRect(0, 0, finalWidth, finalHeight)
    canvas.width = 0
    canvas.height = 0

    setCapturedImage(imageSrc)
  }

  const retakePhoto = () => {
    setPatronScanError({})
    setCapturedImage(null)
    setIsResultPreviewClose(false)
  }
  const previewResults = () => {
    setCapturedImage(null)
    setIsResultPreviewClose(true)
  }
  const callPatronScan = (croppedImage) => {
    setPatronScanError({})
    setIsLoading(true)
    const base64 = croppedImage.split(',')[1]

    const payload = {
      image: base64,
    }

    scanImage(payload)
      .then((result) => {
        setIsLoading(false)
        if (result?.success) {
          setPatronScanResponse(result)

          previewResults()
        } else {
          setPatronScanError(result?.error)
          console.log('error', result?.error)
        }
      })
      .catch((error) => {
        setIsLoading(false)
        console.log('error', error)
        setPatronScanError(error?.error)
      })
  }
  const closePreviewModal = () => {
    setCapturedImage(null)
    setIsResultPreviewClose(false)
    setPatronScanError({})
  }

  const closePreviewResultsModal = () => {
    setIsResultPreviewClose(false)
    setIsCameraOpen(false)
  }

  const [facingMode, setFacingMode] = useState('environment') // "user" or "environment"
  const [videoConstraints, setVideoConstraints] = useState(null)

  const initCamera = async (facing = 'environment') => {
    try {
      // Try one flexible request
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 9999 },
          height: { ideal: 9999 },
          facingMode: { ideal: facing },
        },
      })

      const track = stream.getVideoTracks()[0]
      const settings = track.getSettings()

      setVideoConstraints({
        width: settings.width,
        height: settings.height,
        facingMode: { ideal: facing },
      })
      console.log(`Maximum available resolution : ${settings.width}x${settings.height}`)
      track.stop() // Don't hold the stream; react-webcam will start its own
    } catch (err) {
      console.error('Failed to access camera:', err)
      const newMode = facingMode === 'environment' ? 'user' : 'environment'
      setFacingMode(newMode)
    }
  }
  useEffect(() => {
    initCamera(facingMode) // on initial load
    return () => {
      if (webcamRef.current?.video?.srcObject) {
        const tracks = webcamRef.current.video.srcObject.getTracks()
        tracks.forEach((track) => track.stop())
      }
    }
  }, [facingMode])

  useEffect(() => {
    const setVh = () => {
      const vh = window.innerHeight * 0.01
      document.documentElement.style.setProperty('--vh', `${vh}px`)
    }

    setVh()
    window.addEventListener('resize', setVh)

    return () => {
      window.removeEventListener('resize', setVh)
    }
  }, [])
  useEffect(() => {
    if (isCameraOpen) document.body.style.overflow = 'hidden'
    else document.body.style.overflow = 'auto'
  }, [isCameraOpen])

  console.log('facingmode', facingMode, videoConstraints)

  const toggleCamera = async () => {
    const stream = webcamRef.current?.stream
    if (stream) {
      stream.getTracks().forEach((track) => track.stop()) // stop camera
    }
    const newMode = facingMode === 'environment' ? 'user' : 'environment'
    setFacingMode(newMode)
    initCamera(newMode)
  }

  return (
    <>
      {/* Button to open camera */}
      <Tooltip>
        <Logo
          src={require(`static/images/barcodescan.svg`)}
          raw
          onClick={() => (isOnline ? setIsCameraOpen(true) : null)}
          isdisabled={+!isOnline}
        />
        {!isOnline && <TooltipText className="tooltip-text">Internet bandwidth is insufficient</TooltipText>}
      </Tooltip>
      {/* Camera UI Overlay */}

      {isCameraOpen && (
        <CameraOverlay>
          {isResultPreviewOpen && (
            <ResultsPreviewModal
              closePreviewModal={closePreviewResultsModal}
              retakePhoto={retakePhoto}
              resultData={patronScanResponse}
            ></ResultsPreviewModal>
          )}
          {capturedImage ? (
            <PreviewModal
              closePreviewModal={closePreviewModal}
              retakePhoto={retakePhoto}
              capturedImage={capturedImage}
              callPatronScan={callPatronScan}
              patronScanError={patronScanError}
              isCallingAPI={isLoading}
            />
          ) : (
            !isResultPreviewOpen && (
              <CameraWrap>
                <CameraContainer>
                  <CloseButton onClick={() => setIsCameraOpen(false)}>
                    <Icon icon={'cross-orange'} customSize="80px" />
                  </CloseButton>
                  {flashEffect && <FlashEffect />}

                  <Webcam
                    // key={facingMode}
                    audio={false}
                    ref={webcamRef}
                    screenshotFormat="image/png"
                    screenshotQuality={1}
                    videoConstraints={videoConstraints}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />

                  <RectangleOverlay contentText={'Place barcode here and capture without blur'} />

                  <Controls>
                    <ControlWrapDiv>
                      <SwitchCameraButton onClick={toggleCamera}>
                        <Icon icon={'camera-rotate'} customSize="40px" />
                      </SwitchCameraButton>
                    </ControlWrapDiv>
                    <ControlWrapDiv>
                      <CaptureButton onClick={capturePhoto} />
                    </ControlWrapDiv>
                  </Controls>
                </CameraContainer>
              </CameraWrap>
            )
          )}
        </CameraOverlay>
      )}
    </>
  )
}

export default React.memo(CameraScan)
